/* eslint-disable no-nested-ternary */
import * as React from 'react';
import PropTypes from 'prop-types';
import { EventService } from '@pingum/api-services';
import { useSelector } from 'react-redux';
import moment from 'moment';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import Skeleton from '@mui/material/Skeleton';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import History from '@mui/icons-material/History';
import {
  ListItemButton,
  ListItemSecondaryAction,
  ListItemAvatar,
  Avatar,
  Chip,
} from '@mui/material';
import {
  AccessAlarm,
  AccessTime,
  CheckBox,
  CheckBoxOutlineBlank,
  ChevronRight,
  InfoOutlined,
  LocationOn,
} from '@mui/icons-material';

import { getBarnHorse } from '../../../../../../components/Schedule/ScheduleTaskItem';
import Horse from '../../../../../../components/Horse';
import { primary } from '../../../../../../utils/brandColors';
import TaskStateLabel from '../../../../../../components/TaskStateLabel';
import { getRelativeTime } from '../../../../../../utils/dateHelper';
import TagReminderService from '../../../../../../services/TagReminderService';

const loadTaskRecords = async (reminderId, eventTemplateId, tag, knownTask) => {
  if (reminderId) {
    try {
      const records = await TagReminderService.getTagReminderEvents(reminderId);
      if (!Array.isArray(records)) throw new Error('Unexpected task history response');
      return records;
    } catch (error) {
      const response = error?.response || error;
      const status = response?.status;
      const path = response?.data?.path;
      // Older APIs report missing routes through their generic 400 handler instead of a 404.
      const legacyRouteError = status === 400
        && response?.data?.message === 'There was an error with the request, please try again later.'
        && typeof path === 'string' && path.endsWith(`/tagReminders/${reminderId}/events`);
      if (status !== 404 && status !== 405 && !legacyRouteError) throw error;
    }
  }
  const result = await EventService.searchEvents({
    eventTemplateId,
    includeComplete: true,
    includeArchived: false,
    tagConditions: [[tag]],
    presetDateFilter: null,
  }, 0, 20, true);
  if (!Array.isArray(result?.content)) throw new Error('Unexpected task history response');
  if (!knownTask?.id) return result.content;
  // The reminder comes from SQL and may already contain a change not yet visible in search.
  const records = result.content.filter((task) => String(task.id) !== String(knownTask.id));
  if (!knownTask.archived) records.push(knownTask);
  return records;
};

const getValidDate = (value) => {
  if (!value) return null;
  const date = moment(value, moment.ISO_8601, true);
  return date.isValid()
    ? date
    : null;
};

const getCompactTask = (task) => {
  const completed = !!task.completeDate;
  let date = getValidDate(task.completeDate);
  let label = 'Completed';
  let unavailableLabel = 'Completion date unavailable';
  if (!completed) {
    date = getValidDate(task.dueDate);
    label = 'Due';
    unavailableLabel = 'Task date unavailable';
    if (!date) {
      date = getValidDate(task.startDate);
      label = 'Started';
    }
  }
  return {
    task,
    completed,
    date,
    label: date
      ? `${label} ${date.format('MMMM D, YYYY')}`
      : unavailableLabel,
  };
};

const TaskHistory = ({
  eventTemplate,
  tag,
  anchorEl,
  onClose,
  inline,
  handleTaskSelect,
  tasks: propTasks,
  barn,
  refreshKey,
  compact,
  disabled,
  readOnly,
  reminderId,
  knownTask,
}) => {
  const [history, setHistory] = React.useState({
    contextKey: null,
    tasks: [],
    isLoading: false,
    hasLoaded: false,
    error: '',
  });
  const [retry, setRetry] = React.useState(0);

  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const reduxBarn = useSelector((state) => state.barn.selectedBarn);
  const selectedBarn = barn || reduxBarn; // Public page wont' have this
  const barnId = selectedBarn?.id;
  const tenant = selectedBarn?.tenant;
  const templateId = eventTemplate?.id;
  const contextKey = JSON.stringify([barnId, tenant, templateId, tag, reminderId]);
  const canLoad = !!(templateId && tag);

  React.useEffect(() => {
    let active = true;
    if (propTasks) {
      setHistory({
        contextKey,
        tasks: propTasks,
        isLoading: false,
        hasLoaded: true,
        error: '',
      });
    } else if (canLoad) {
      setHistory((previous) => ({
        contextKey,
        tasks: previous.contextKey === contextKey
          ? previous.tasks
          : [],
        hasLoaded: previous.contextKey === contextKey && previous.hasLoaded,
        isLoading: true,
        error: '',
      }));
      loadTaskRecords(reminderId, templateId, tag, knownTask).then((records) => {
        if (!active) return;
        setHistory({
          contextKey,
          tasks: records,
          isLoading: false,
          hasLoaded: true,
          error: '',
        });
      }).catch(() => {
        if (!active) return;
        setHistory((previous) => ({
          ...previous,
          isLoading: false,
          error: 'Task records could not be loaded.',
        }));
      });
    } else {
      setHistory({
        contextKey,
        tasks: [],
        isLoading: false,
        hasLoaded: true,
        error: '',
      });
    }
    return () => { active = false; };
  }, [contextKey, canLoad, templateId, tag, propTasks, refreshKey, retry, reminderId, knownTask]);

  const current = history.contextKey === contextKey;
  const tasks = current
    ? history.tasks
    : [];
  const isLoading = current
    ? history.isLoading
    : canLoad && !propTasks;
  const hasLoaded = current && history.hasLoaded;
  const error = current
    ? history.error
    : '';
  const isInitialLoading = isLoading && !hasLoaded;
  const isRefreshing = isLoading && hasLoaded;
  const actionsDisabled = disabled || isLoading;

  const refreshIndicator = isRefreshing && (
    <LinearProgress
      aria-label="Refreshing task history"
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 2,
      }}
    />
  );

  const selectTask = (task) => {
    if (!readOnly && !actionsDisabled) handleTaskSelect(task);
  };
  const HistoryRow = readOnly
    ? ListItem
    : ListItemButton;
  const getRowProps = (task, label) => {
    if (readOnly) return {};
    return {
      onClick: () => selectTask(task),
      disabled: actionsDisabled,
      'aria-label': label,
    };
  };

  const getCompactTasksComponent = () => {
    const sortedTasks = tasks.map(getCompactTask).sort((left, right) => {
      if (!left.date) return Number(!!right.date);
      if (!right.date) return -1;
      return right.date.valueOf() - left.date.valueOf();
    });
    return (
      <Box aria-busy={isLoading} sx={{ position: 'relative' }}>
        {refreshIndicator}
        {error && (
          <Alert severity="error" action={<Button disabled={actionsDisabled} onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>
            {error}
          </Alert>
        )}
        {isInitialLoading && (
          <Box aria-label="Loading task history" sx={{ py: 1 }}>
            <Skeleton height={48} />
            <Skeleton height={48} />
            <Skeleton height={48} />
          </Box>
        )}
        {sortedTasks.length > 0 && (
          <List
            disablePadding
            aria-label="Task history"
            sx={{
              maxHeight: 320,
              overflow: 'auto',
            }}
          >
            {sortedTasks.map(({
              task,
              completed,
              date,
              label,
            }) => {
              const status = completed
                ? 'Completed'
                : 'Incomplete';
              const statusColor = completed
                ? 'success'
                : 'default';
              const relativeDate = date
                ? date.fromNow()
                : null;
              return (
                <HistoryRow
                  key={`task-history-${task.id}`}
                  {...getRowProps(task, `Open task: ${label}`)}
                  dense
                  divider
                  sx={{
                    px: 1,
                    py: 0.75,
                    gap: 1,
                  }}
                >
                  <ListItemText
                    primary={label}
                    secondary={relativeDate}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                    sx={{ minWidth: 0 }}
                  />
                  <Chip size="small" label={status} color={statusColor} variant="outlined" />
                  {!readOnly && <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />}
                </HistoryRow>
              );
            })}
          </List>
        )}
        {!isInitialLoading && !error && sortedTasks.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No task history yet.</Typography>
        )}
      </Box>
    );
  };

  const getLegacyTasksComponent = () => (
    <Box aria-busy={isLoading} sx={{ position: 'relative' }}>
      {refreshIndicator}
      {error && (
        <Alert severity="error" action={<Button disabled={actionsDisabled} onClick={() => setRetry(retry + 1)}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {
        !inline && (
          <ListItem dense divider>
            <ListItemIcon><History /></ListItemIcon>
            <ListItemText
              primary={`Task History for ${(eventTemplate && eventTemplate.name) || ''}`}
            />
          </ListItem>
        )
      }
      {
        isInitialLoading && (
          <Box>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
          </Box>
        )
      }
      {
        tasks.length > 0 && (
          <List
            sx={{
              maxHeight: 350,
              overflow: 'auto',
            }}
          >
            {tasks.map((task) => {
              const horseTagTypeId = (selectedBarn.barnMetadata && selectedBarn.barnMetadata.horseTagTypeId) || 0;
              const barnHorse = getBarnHorse(task, null, barnHorsesMap, horseTagTypeId);
              const priorDayTask = task && new Date(task.startDate) < moment().startOf('day').toDate().getTime();

              const color = (task.eventData && task.eventData.Color && task.eventData.Color.value) || primary[500]

              return (
                <HistoryRow
                  key={`task-history-${task.id}`}
                  alignItems="center"
                  sx={{
                    borderLeft: `8px solid ${color}`,
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                    my: 0.5,
                  }}
                  divider
                  {...getRowProps(task)}
                  dense
                >
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    {
                      barnHorse
                        ? (
                          <Horse
                            horse={barnHorse.horse}
                            hideName
                            size={30}
                            clickable={!readOnly}
                          />
                        )
                        : (
                          <Avatar
                            aria-label="recipe"
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: '10pt',
                            }}
                          >
                            <LocationOn />
                          </Avatar>
                        )
                    }
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={(
                      <Typography variant="body1" sx={{ lineHeight: 1 }} noWrap>
                        {task.name}
                      </Typography>
                    )}
                    secondary={(
                      <Box
                        style={{
                          display: 'flex',
                          marginTop: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <TaskStateLabel
                          archived={task && !!task.archived}
                          completed={task && !!task.completeDate}
                          // eslint-disable-next-line no-nested-ternary
                          dueDate={task
                            ? task.dueDate
                            : null}
                          priorDayTask={priorDayTask}
                          createdDate={task && task.startDate}
                          isTaskScheduled={false}
                          isTaskCreated
                          hideComplete
                        />
                        {
                          (task.dueDate && new Date(task.dueDate).getTime() < new Date().getTime())
                            ? (
                              <AccessAlarm
                                sx={{
                                  fontSize: 18,
                                  marginRight: 0.5,
                                }}
                                color="secondary"
                              />
                            )
                            : (
                              <AccessTime
                                sx={{
                                  fontSize: 18,
                                  marginRight: 0.5,
                                }}
                                color="secondary"
                              />
                            )
                        }

                        <Typography
                          sx={{ display: 'inline' }}
                          component="span"
                          variant="caption"
                          color="text.primary"
                          noWrap
                        >
                          {task.completeDate
                            ? (`completed ${moment(task.completeDate).fromNow()}`)
                            : (task.dueDate && new Date(task.dueDate).getTime() < new Date().getTime())
                              ? `due ${getRelativeTime(new Date(), task.dueDate)} ago`
                              : `${getRelativeTime(new Date(), task.startDate)} old`}

                        </Typography>
                      </Box>
                    )}
                  />
                  <ListItemSecondaryAction sx={{ right: 6 }}>
                    {
                      task.completeDate
                        ? (
                          <CheckBox
                            sx={{ color }}
                          />
                        )
                        : (
                          <CheckBoxOutlineBlank
                            sx={{ color }}
                          />
                        )
                    }
                  </ListItemSecondaryAction>
                </HistoryRow>
              )
            })}
          </List>
        )
      }
      {
        (!isInitialLoading && !error && tasks.length === 0) && (
          <Box
            sx={{
              textAlign: 'center',
              m: 4,
            }}
          >
            <InfoOutlined
              sx={{ fontSize: 50 }}
              color="secondary"
            />
            <Typography
              variant="subtitle1"
              align="center"
              component="div"
              sx={{
                my: 2,
                fontWeight: 300,
              }}
            >
              No Tasks
            </Typography>
          </Box>
        )
      }
    </Box>
  );

  const getTasksComponent = () => {
    if (compact) return getCompactTasksComponent();
    return getLegacyTasksComponent();
  };

  if (inline) {
    return (
      getTasksComponent()
    )
  }
  return (
    <Popover
      id="task-history-quick-view"
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      sx={{
        minWidth: '400px !important',
        p: 2,
      }}
    >
      {getTasksComponent()}
    </Popover>
  );
};

TaskHistory.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  eventTemplate: PropTypes.object,
  tag: PropTypes.number,
  inline: PropTypes.bool,
  handleTaskSelect: PropTypes.func,
  tasks: PropTypes.array,
  barn: PropTypes.object,
  refreshKey: PropTypes.number,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  reminderId: PropTypes.number,
  knownTask: PropTypes.object,
};

TaskHistory.defaultProps = {
  anchorEl: null,
  eventTemplate: null,
  tag: null,
  inline: false,
  handleTaskSelect: () => { },
  onClose: () => { },
  tasks: null,
  barn: null,
  refreshKey: 0,
  compact: false,
  disabled: false,
  readOnly: false,
  reminderId: null,
  knownTask: null,
};

export default TaskHistory;
